/**
 * Building API functions.
 *
 * Maps to backend scenario-service endpoints:
 *   POST   /api/communities/:cid/buildings          - Create building
 *   GET    /api/communities/:cid/buildings          - List buildings
 *   GET    /api/communities/:cid/buildings/:bid     - Get building (with assets)
 *   PUT    /api/communities/:cid/buildings/:bid     - Update building
 *   DELETE /api/communities/:cid/buildings/:bid     - Delete building
 */

import client from './client';

/**
 * List all buildings for a community.
 * @param {string} communityId - UUID
 * @returns {Promise<Array>}
 */
export const getBuildings = async (communityId) => {
  return client.get(`/api/communities/${communityId}/buildings`);
};

/**
 * Get a single building with its assets and datasets.
 * @param {string} communityId - UUID
 * @param {string} buildingId - UUID (internal PK, not the user-defined building_id)
 * @returns {Promise<Object>}
 */
export const getBuilding = async (communityId, buildingId) => {
  return client.get(`/api/communities/${communityId}/buildings/${buildingId}`);
};

/**
 * Create a new building in a community.
 * @param {string} communityId - UUID
 * @param {Object} data - { building_id, building_type, floor_area_m2, energy_class, metadata }
 * @returns {Promise<Object>} Created building
 */
export const createBuilding = async (communityId, data) => {
  return client.post(`/api/communities/${communityId}/buildings`, data);
};

/**
 * Update a building (partial).
 * @param {string} communityId - UUID
 * @param {string} buildingId - UUID (internal PK)
 * @param {Object} data - Fields to update
 * @returns {Promise<Object>} Updated building
 */
export const updateBuilding = async (communityId, buildingId, data) => {
  return client.put(`/api/communities/${communityId}/buildings/${buildingId}`, data);
};

/**
 * Delete a building (cascade-deletes building_assets).
 * @param {string} communityId - UUID
 * @param {string} buildingId - UUID (internal PK)
 * @returns {Promise<void>}
 */
export const deleteBuilding = async (communityId, buildingId) => {
  return client.delete(`/api/communities/${communityId}/buildings/${buildingId}`);
};
